import React from 'react';
import classNames from 'classnames';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { FaSearch, FaTimes, FaPlus, FaFile } from 'react-icons/fa';
import { mask, unMask } from 'remask';
import { DropdownInputGroup } from '../../../../styles/primeReactConfig.js';
import useTerminologiaRecorrencia from '../../../../hooks/useTerminologiaRecorrencia.js';

/**
 * Passthrough dos dropdowns de filtro.
 */
const dropdownFiltrosPt = {
    ...DropdownInputGroup,
    root: ({ props }) => ({
        className: classNames(
            'cursor-pointer inline-flex relative select-none h-12',
            'bg-white border border-gray-300 transition-colors duration-200 ease-in-out rounded-md',
            'dark:bg-gray-900 dark:border-blue-900/40 dark:hover:border-blue-300',
            'w-full',
            'hover:border-blue-500 focus:outline-none focus:outline-offset-0 focus:shadow-[0_0_0_0.2rem_rgba(191,219,254,1)] dark:focus:shadow-[0_0_0_0.2rem_rgba(147,197,253,0.5)]',
            {
                'opacity-60 select-none pointer-events-none cursor-default': props?.disabled
            }
        )
    })
};

/**
 * Passthrough do campo de busca.
 */
const inputBuscaPt = {
    root: ({ props, context }) => ({
        className: classNames(
            'm-0 w-full min-w-[10rem] h-12',
            'font-sans text-gray-600 dark:text-white/80 bg-white dark:bg-gray-900 border transition-colors duration-200 appearance-none rounded-md',
            'focus:outline-none focus:outline-offset-0 focus:shadow-[0_0_0_0.2rem_rgba(191,219,254,1)] dark:focus:shadow-[0_0_0_0.2rem_rgba(147,197,253,0.5)]',
            'hover:border-blue-500',
            'p-3 text-base',
            {
                'border-gray-300 dark:border-blue-900/40': !props?.invalid,
                'border-red-500 hover:border-red-500/80 focus:border-red-500':
                    props?.invalid && !context?.disabled,
                'opacity-60 select-none pointer-events-none cursor-default':
                context?.disabled
            }
        )
    })
};

const OPCOES_STATUS = [
    { label: 'Todos', value: 'todos' },
    { label: 'Ativos', value: 'ativos' },
    { label: 'Inativos', value: 'inativos' }
];

/**
 * Retorna o placeholder do campo de busca conforme o filtro selecionado.
 *
 * @param {string} selectedFilter
 * @returns {string}
 */
function getBuscaPlaceholder(selectedFilter) {
    switch (selectedFilter) {
        case 'usuario_documento':
            return 'Buscar por CPF/CNPJ';

        case 'beneficiario':
            return 'Buscar por beneficiário';

        case 'contratante_numero':
            return 'Buscar por nº cliente';

        case 'usuario_nome':
        default:
            return 'Buscar por nome';
    }
}

/**
 * Cabeçalho com filtros e ações da listagem de contratantes.
 *
 * @param {object} props
 * @param {object|null} props.credorData
 * @param {string} props.selectedFilter
 * @param {(value: string) => void} props.setSelectedFilter
 * @param {string} props.filterParam
 * @param {(value: string) => void} props.setFilterParam
 * @param {(keyCode: number) => void} props.handleSearch
 * @param {(updater: Function) => void} props.setReload
 * @param {(value: boolean) => void} props.setSearchState
 * @param {(updater: Function) => void} props.setShowModals
 * @param {string|null} props.grupoFilter
 * @param {(value: string|null) => void} props.setGrupoFilter
 * @param {string} props.statusFilter
 * @param {(value: string) => void} props.setStatusFilter
 * @param {Array<{label: string, value: string}>} props.gruposOptions
 * @param {() => void} [props.onExportar]
 * @param {boolean} [props.isExporting]
 * @returns {JSX.Element | null}
 */
export default function HeaderDeFiltrosContratante({
                                                       credorData,
                                                       selectedFilter,
                                                       setSelectedFilter,
                                                       filterParam,
                                                       setFilterParam,
                                                       handleSearch,
                                                       setReload,
                                                       setSearchState,
                                                       setShowModals,
                                                       grupoFilter,
                                                       setGrupoFilter,
                                                       statusFilter,
                                                       setStatusFilter,
                                                       gruposOptions,
                                                       onExportar,
                                                       isExporting
                                                   }) {
    const terminologia = useTerminologiaRecorrencia();

    const filtroCredor = [
        { label: 'Nº Cliente', value: 'contratante_numero' },
        { label: 'Por Nome', value: 'usuario_nome' },
        { label: 'Por Beneficiário', value: 'beneficiario' },
        { label: 'Por Documento', value: 'usuario_documento' }
    ];

    const btnIconClass =
        'h-12 w-12 rounded-md flex justify-center items-center transition duration-300';
    const btnActionClass =
        '!h-12 min-h-[3rem] rounded-md px-4 whitespace-nowrap flex items-center justify-center';

    /**
     * Atualiza o valor do campo de busca.
     *
     * @param {React.ChangeEvent<HTMLInputElement>} event
     * @returns {void}
     */
    function handleBuscaChange(event) {
        if (selectedFilter === 'usuario_documento') {
            setFilterParam(
                mask(unMask(event.target.value), [
                    '999.999.999-99',
                    '99.999.999/9999-99'
                ])
            );
            return;
        }

        setFilterParam(event.target.value);
    }

    /**
     * Dispara a busca ao pressionar Enter.
     *
     * @param {React.KeyboardEvent<HTMLInputElement>} event
     * @returns {void}
     */
    function handleBuscaKeyDown(event) {
        if (event.key === 'Enter') {
            handleSearch(13);
        }
    }

    /**
     * Limpa os filtros da tela e recarrega a listagem padrão.
     *
     * @returns {void}
     */
    function handleClearFilters() {
        setFilterParam('');
        setGrupoFilter?.(null);
        setStatusFilter?.('ativos');
        setReload((prev) => !prev);
        setSearchState(false);
    }

    /**
     * Renderiza o campo de busca conforme o tipo selecionado.
     *
     * @returns {JSX.Element}
     */
    function renderInput() {
        return (
            <InputText
                value={filterParam}
                onChange={handleBuscaChange}
                placeholder={getBuscaPlaceholder(selectedFilter)}
                onKeyDown={handleBuscaKeyDown}
                pt={inputBuscaPt}
            />
        );
    }

    if (!credorData) {
        return null;
    }

    return (
        <div className="w-full">
            <div className="mb-4">
                <h2 className="m-0 text-2xl font-semibold text-gray-800 dark:text-white/90">
                    Clientes
                </h2>
            </div>

            <div className="rounded-xl border border-gray-200 bg-slate-50/80 p-4 dark:border-blue-900/40 dark:bg-gray-950/30">
                <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
                    <div className="grid flex-1 grid-cols-1 gap-3 md:grid-cols-2 2xl:grid-cols-3">
                        <div className="flex flex-col gap-1">
                            <label className="text-sm font-medium text-gray-700 dark:text-white/80">
                                {terminologia.labelGrupo}
                            </label>
                            <Dropdown
                                value={grupoFilter ?? null}
                                options={gruposOptions}
                                onChange={(e) => setGrupoFilter(e.value)}
                                placeholder="Todos"
                                filter
                                filterPlaceholder={terminologia.placeholderBuscarGrupo}
                                pt={dropdownFiltrosPt}
                            />
                        </div>

                        <div className="flex flex-col gap-1">
                            <label className="text-sm font-medium text-gray-700 dark:text-white/80">
                                Buscar por
                            </label>
                            <Dropdown
                                value={selectedFilter}
                                options={filtroCredor}
                                onChange={(e) => {
                                    setSelectedFilter(e.value);
                                    setFilterParam('');
                                }}
                                pt={dropdownFiltrosPt}
                            />
                        </div>

                        <div className="flex flex-col gap-1">
                            <label className="text-sm font-medium text-gray-700 dark:text-white/80">
                                Status
                            </label>
                            <Dropdown
                                value={statusFilter ?? 'ativos'}
                                options={OPCOES_STATUS}
                                onChange={(e) => setStatusFilter(e.value)}
                                pt={dropdownFiltrosPt}
                            />
                        </div>

                        <div className="flex flex-col gap-1 md:col-span-2 2xl:col-span-3">
                            <label className="text-sm font-medium text-gray-700 dark:text-white/80">
                                Busca
                            </label>

                            <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
                                <div className="min-w-0 flex-1">
                                    {renderInput()}
                                </div>

                                <div className="flex shrink-0 flex-row gap-2">
                                    <Button
                                        icon={<FaSearch className="text-xl" />}
                                        onClick={() => handleSearch(13)}
                                        tooltip="Pesquisar"
                                        tooltipOptions={{ position: 'top' }}
                                        pt={{
                                            root: `hover:dark:bg-green-500 hover:bg-green-600 dark:bg-green-400 bg-green-500 p-3 rounded-md text-white dark:text-black ${btnIconClass}`,
                                            label: 'absolute'
                                        }}
                                    />

                                    <Button
                                        icon={<FaTimes className="text-2xl" />}
                                        onClick={handleClearFilters}
                                        tooltip="Limpar filtros"
                                        tooltipOptions={{ position: 'top' }}
                                        pt={{
                                            root: `hover:dark:bg-red-500 hover:bg-red-600 dark:bg-red-400 bg-red-500 p-3 rounded-md text-white dark:text-black ${btnIconClass}`,
                                            label: 'absolute'
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex shrink-0 flex-col gap-2 sm:flex-row xl:self-end">
                        <Button
                            label="Novo Cliente"
                            icon={<FaPlus className="mr-2" />}
                            onClick={() => setShowModals((prev) => ({ ...prev, create: true }))}
                            className={btnActionClass}
                        />

                        <Button
                            label="Exportar"
                            icon={<FaFile className="mr-2" />}
                            onClick={() => onExportar?.()}
                            disabled={isExporting}
                            loading={isExporting}
                            className={btnActionClass}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}